// apps/share-web/src/offline/prefetch.ts
//
// Докачка слов всех песен команды — то, ради чего волна и затевалась: «чтобы
// кэшировались ВСЕ слова, чтобы можно было в момент зайти и посмотреть».
//
// Три свойства, каждое со своей причиной:
//
//   ПО ОДНОЙ. Телефон в зале сидит на перегруженной сотовой сети за общим
//   NAT. Веер из ста запросов там не быстрее — он медленнее и роняет часть.
//
//   ТОЛЬКО ИЗМЕНИВШЕЕСЯ. `updatedAt` приезжает в списке библиотеки готовым
//   (`SongTextSummary.updatedAt`), поэтому сверка версий не стоит ни одного
//   запроса: качаем лишь то, чего в кэше нет или что поправили.
//
//   ОТМЕНЯЕМАЯ. Без отмены уход со страницы оставляет докачку писать в
//   хранилище человека, которого на экране уже нет.
//
// Отказ по одной песне не останавливает остальные: девять скачанных лучше
// одной скачанной и стопа на второй. Это верно для ОБОИХ видов отказа —
// сетевого (`fetchOne` вернул `ok: false`) и отказа ЗАПИСИ (`saveWords`
// бросил, например `QuotaExceededError` на переполненном телефоне): оба
// обработаны одинаково, `continue` на следующую песню. Сигнал проверяется
// ПЕРЕД каждой песней, а не только в начале: отмена приходит посреди обхода,
// и смысл её в том, чтобы следующего запроса не было.
//
// `songTexts` необязательное (см. library.ts — воркер и веб выкатываются
// порознь). Его отсутствие здесь читается как «качать нечего», и это
// безопасно: докачка ничего не удаляет. Удаление — дело сверки, и там то же
// отсутствие читается противоположным образом, «не знаю» (reconcile.ts).

import type { OfflineStore } from './offline-store.js';
import type { TeamLibrary } from '../library.js';
import { upcomingSetlistIds, isStale, wordsIdsOf, SETLIST_KEY_TEAM, type UpcomingSetlistRef } from './setlist-offline.js';

export type FetchOneWords = (songTextId: string) =>
  Promise<{ ok: true; payload: unknown } | { ok: false }>;

export interface PrefetchArgs {
  store: OfflineStore;
  teams: TeamLibrary[];
  fetchOne: FetchOneWords;
  signal?: AbortSignal;
}

/** Общий обход для `prefetchWords`/`prefetchWordsByIds` — те же три свойства
 *  из шапки файла (по одной, только изменившееся, отменяемая), `only ===
 *  null` — вся библиотека (`prefetchWords`), иначе — только перечисленные id
 *  (`prefetchWordsByIds`, задача D4). */
async function runWordsPrefetch(args: PrefetchArgs, only: Set<string> | null): Promise<void> {
  for (const team of args.teams) {
    for (const song of team.songTexts ?? []) {
      if (only && !only.has(song.id)) continue;
      if (args.signal?.aborted) return;
      const have = await args.store.loadWords(team.teamId, song.id);
      if (have && have.updatedAt === song.updatedAt) continue;
      const got = await args.fetchOne(song.id);
      if (!got.ok) continue;
      try {
        await args.store.saveWords(team.teamId, song.id, {
          updatedAt: song.updatedAt, payload: got.payload,
        });
      } catch {
        // Тот же принцип, что у сетевого отказа выше: одна неудачная запись
        // (например `QuotaExceededError`) не имеет права остановить докачку
        // ВСЕХ оставшихся песен прохода.
        continue;
      }
    }
  }
}

export async function prefetchWords(args: PrefetchArgs): Promise<void> {
  return runWordsPrefetch(args, null);
}

/**
 * Единый сетлист, фазы 3+4, задача D4 — вход докачки слов ПО СПИСКУ id, а не
 * по всей библиотеке: `prefetchSetlists` (ниже) возвращает id слов ИЗ состава
 * предстоящих сетлистов, и этим словам нужен приоритет перед общей докачкой
 * (`prefetchWords`, качает всю команду без порядка). Команда/`updatedAt`
 * каждой песни всё равно берутся из `teams` (`TeamLibrary.songTexts`) — id из
 * состава сетлиста это тот же `song_texts.id`
 * (`setlist-songs-view.ts::buildSetlistSongsView` на воркере), но САМ по себе
 * не несёт ни команды, ни версии, чтобы решить, устарел ли кэш.
 *
 * `ids`, которых нет ни в одной команде `teams` (устаревшая версия
 * библиотеки, песню сняли между ответом сетлиста и этим вызовом), тихо
 * пропускаются — докачивать нечего, и это не отказ.
 */
export async function prefetchWordsByIds(
  args: PrefetchArgs & { ids: Iterable<string> },
): Promise<void> {
  const only = new Set(args.ids);
  if (only.size === 0) return;
  return runWordsPrefetch(args, only);
}

// ── Состав предстоящих сетлистов (задача C5 волны G) ───────────────────────
//
// Тот же скелет, что у `prefetchWords` выше (по одной, только изменившееся,
// отменяемая, отказ одного не топит остальные) — только версия сверяется НЕ
// телом ответа, а `updatedAt` из самой библиотеки (`isStale`,
// setlist-offline.ts), ровно как у слов сверяется `SongTextSummary.updatedAt`.
//
// `songs[].words.id` докачанных (и уже свежих) составов возвращается вызывающему
// одним списком — он и есть вход докачки слов (`prefetchWords` выше): без
// него «прочитать предстоящий сетлист офлайн» осталось бы наполовину сделанным
// — состав есть, а слова каждой песни всё ещё требуют сети. Проводка,
// вызывающая обе докачки по очереди (main.ts, задача D — за периметром C5),
// строит `PrefetchArgs.teams` из этого списка.

/**
 * `reason` — необязательный, задача D4: сервер СКАЗАЛ, что этого сетлиста у
 * человека больше нет (`notFound` — сняли/отозвали) или что доступ закрылся
 * (`needsAuth` — 401, ключ/членство отозваны) — тем же различием, что
 * `WordsFetchReason` у слов (words-resolve.ts): «сеть подвела» (reason
 * отсутствует) кэш не трогает, а этим двум причинам он не должен пережить
 * попытку. Опциональность — чтобы старые вызовы (без различения причин
 * отказа, только `{ok:false}`) не ломались типом.
 */
export type FetchOneSetlist = (setlistId: string) =>
  Promise<{ ok: true; payload: unknown } | { ok: false; reason?: 'notFound' | 'needsAuth' }>;

/**
 * Подменяет отбор «предстоящих» сетлистов — тем же приёмом, что `isOffline`
 * у `resolveWords` (words-resolve.ts): по умолчанию дата берётся с системных
 * часов (`upcomingSetlistIds(library, new Date())`, setlist-offline.ts), а
 * тест подменяет отбор целиком, не подделывая системное время ради границы
 * «сегодня» (её тестом уже закрыт сам `upcomingSetlistIds`/
 * `splitAndSortSetlists` — здесь переиспытывать нечего).
 */
export type UpcomingSelector = (library: TeamLibrary[]) => UpcomingSetlistRef[];

export interface PrefetchSetlistsArgs {
  store: OfflineStore;
  library: TeamLibrary[];
  fetchSetlist: FetchOneSetlist;
  signal?: AbortSignal;
  isUpcoming?: UpcomingSelector;
}

export async function prefetchSetlists(args: PrefetchSetlistsArgs): Promise<string[]> {
  const pick = args.isUpcoming ?? ((library: TeamLibrary[]) => upcomingSetlistIds(library, new Date()));
  const refs = pick(args.library);

  for (const ref of refs) {
    if (args.signal?.aborted) break;
    // `SETLIST_KEY_TEAM`, НЕ `ref.teamId` (исправление задачи D4): ключ
    // этого вида team-agnostic (см. её doc-комментарий, setlist-offline.ts)
    // — иначе экран `/s/<uuid>`, читающий под `''`, никогда не находит то,
    // что докачка положила под настоящей командой.
    const have = await args.store.loadSetlist(SETLIST_KEY_TEAM, ref.summary.setlistId);
    if (!isStale(have, ref.summary)) continue;
    const got = await args.fetchSetlist(ref.summary.setlistId);
    if (!got.ok) {
      // Зеркало `applyFetchResult` (words-resolve.ts): «сетлиста больше нет»
      // / «доступ закрылся» стирают кэш, обычный сетевой отказ (reason
      // отсутствует) — нет, старая копия переживает попытку (см. ветку
      // «слова» ниже, она тоже читает то, что осталось в кэше).
      if (got.reason === 'notFound' || got.reason === 'needsAuth') {
        try { await args.store.delSetlist(SETLIST_KEY_TEAM, ref.summary.setlistId); } catch { /* см. catch у saveSetlist ниже */ }
      }
      continue;
    }
    try {
      await args.store.saveSetlist(SETLIST_KEY_TEAM, ref.summary.setlistId, {
        updatedAt: ref.summary.updatedAt, payload: got.payload,
      });
    } catch {
      // Тот же принцип, что у слов: неудавшаяся запись не топит остальные
      // сетлисты прохода, и не имеет права оставить кэш в наполовину
      // применённом состоянии — `saveSetlist` либо целиком применился, либо
      // не применился вовсе, `catch` здесь ничего частично не оставляет.
      continue;
    }
  }

  // Слова — из ВСЕГО, что сейчас лежит в кэше под предстоящими сетлистами, а
  // не только из только что докачанных: сетлист, чей состав уже был свежим
  // (`isStale` сказал «нет» выше, сеть не трогали), тоже обязан отдать свои
  // слова докачке слов — иначе она не увидит их НИКОГДА, ведь состав в её
  // адрес не попадает вовсе. Сетлист, чья докачка упала сетевым отказом, но у
  // которого осталась СТАРАЯ копия (не перезаписана — см. `catch`/`!got.ok`
  // выше), тоже отдаёт слова этой старой копии: устаревшие слова лучше, чем
  // никаких, тем же принципом, что у самого кэша слов при сетевом отказе
  // (words-resolve.ts).
  const ids = new Set<string>();
  for (const ref of refs) {
    const cached = await args.store.loadSetlist(SETLIST_KEY_TEAM, ref.summary.setlistId);
    if (cached) for (const id of wordsIdsOf(cached.payload)) ids.add(id);
  }
  return Array.from(ids);
}
